import React from "react";
import { useEffect, useState } from "react";
import styles from "../styles.modules.scss";

export function Timer({
    timerString,
    lang,
}: {
    timerString: string;
    lang: string;
}) {
    const [remainingTime, setRemainingTime] = useState<{
        remainingSeconds: number | null;
        remainingDays: number;
        remainingHours: number;
        remainderMinutes: number;
    }>({
        remainingSeconds: null,
        remainingDays: 0,
        remainingHours: 0,
        remainderMinutes: 0,
    });

    const endTime = timerString ? new Date(timerString).getTime() : 0;

    useEffect(() => {
        const timer = async () => {
            const timeData = {
                secondsLeft: Math.floor((endTime - Date.now()) / 1000),
                secondsLeftTimestampMs:
                    Date.now() +
                    Math.floor((endTime - Date.now()) / 1000) * 1000,
            };

            const remainingSeconds = timeData.secondsLeft;

            let remainingHours = Math.floor(remainingSeconds / 3600);
            let remainingDays = 0;

            if (remainingHours > 23) {
                remainingDays = Math.floor(remainingHours / 24);
                remainingHours = remainingHours % 24;
            }

            const remainderSeconds =
                remainingSeconds -
                remainingDays * 24 * 3600 -
                remainingHours * 3600;

            let remainderMinutes = Math.ceil(remainderSeconds / 60);

            if (remainderMinutes === 60) {
                remainingHours += 1;
                remainderMinutes = 0;
                if (remainingHours === 24) {
                    remainingDays += 1;
                    remainingHours = 0;
                }
            }

            setRemainingTime({
                remainingSeconds,
                remainingDays,
                remainingHours,
                remainderMinutes,
            });
        };
        timer();
        const interval = setInterval(() => {
            timer();
        }, 1000);
        return () => clearInterval(interval);
    }, [endTime]);

    const translate = (
        id: "daysAbbreviated" | "hoursAbbreviated" | "minutesAbbreviated",
    ) => {
        type Translations = {
            daysAbbreviated: string;
            hoursAbbreviated: string;
            minutesAbbreviated: string;
        };

        const translations: {
            [key in string]: Translations;
        } = {
            de: {
                daysAbbreviated: "D",
                hoursAbbreviated: "H",
                minutesAbbreviated: "M",
            },
            en: {
                daysAbbreviated: "d",
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
            },
            es: {
                daysAbbreviated: "d",
                hoursAbbreviated: "h",
                minutesAbbreviated: "min",
            },
            fr: {
                daysAbbreviated: "j",
                hoursAbbreviated: "h",
                minutesAbbreviated: "min",
            },
            it: {
                daysAbbreviated: "g",
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
            },
            ja: {
                daysAbbreviated: "日",
                hoursAbbreviated: "時間",
                minutesAbbreviated: "分",
            },
            ko: {
                daysAbbreviated: "일",
                hoursAbbreviated: "시간",
                minutesAbbreviated: "분",
            },
            pl: {
                daysAbbreviated: "d",
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
            },
            pt: {
                daysAbbreviated: "d",
                hoursAbbreviated: "h",
                minutesAbbreviated: "m",
            },
            ru: {
                daysAbbreviated: " д.",
                hoursAbbreviated: " ч.",
                minutesAbbreviated: " м.",
            },
            tr: {
                daysAbbreviated: "g",
                hoursAbbreviated: "sa",
                minutesAbbreviated: "dk",
            },
            "zh-hans": {
                daysAbbreviated: "天",
                hoursAbbreviated: "小时",
                minutesAbbreviated: "分",
            },
            zh: {
                daysAbbreviated: "天",
                hoursAbbreviated: "小時",
                minutesAbbreviated: "分",
            },
            da: {
                daysAbbreviated: "d",
                hoursAbbreviated: "t",
                minutesAbbreviated: "min",
            },
            ar: {
                daysAbbreviated: " ي",
                hoursAbbreviated: " س",
                minutesAbbreviated: " د",
            },
            fi: {
                daysAbbreviated: "p",
                hoursAbbreviated: "t",
                minutesAbbreviated: "min",
            },
            id: {
                daysAbbreviated: "h",
                hoursAbbreviated: "j",
                minutesAbbreviated: "m",
            },
            ms: {
                daysAbbreviated: "h",
                hoursAbbreviated: "j",
                minutesAbbreviated: "m",
            },
            nl: {
                daysAbbreviated: "d",
                hoursAbbreviated: "u",
                minutesAbbreviated: "m",
            },
            th: {
                daysAbbreviated: "ว.",
                hoursAbbreviated: "ชม.",
                minutesAbbreviated: "น.",
            },
            vi: {
                daysAbbreviated: "ng",
                hoursAbbreviated: "giờ",
                minutesAbbreviated: "ph",
            },
        };

        return translations[lang]?.[id] || translations["en"]?.[id];
    };

    return (
        <div
            className={`maintenanceTimeContainer ${styles.maintenanceTimeContainer}`}
        >
            <MaintenanceClock />
            <div className={`maintenanceTimeBox ${styles.maintenanceTimeBox}`}>
                <div
                    className={`maintenanceEstimatedNumberText ${styles.maintenanceEstimatedNumberText}`}
                >
                    {remainingTime.remainingSeconds !== null ? (
                        remainingTime.remainingSeconds > 0 ? (
                            <>
                                {remainingTime.remainingDays > 0 ? (
                                    <>
                                        {remainingTime.remainingDays}
                                        {translate("daysAbbreviated")}{" "}
                                        {remainingTime.remainingHours}
                                        {translate("hoursAbbreviated")}
                                    </>
                                ) : (
                                    <>
                                        {remainingTime.remainingHours}
                                        {translate("hoursAbbreviated")}{" "}
                                        {remainingTime.remainderMinutes}
                                        {translate("minutesAbbreviated")}
                                    </>
                                )}
                            </>
                        ) : (
                            `0${translate("hoursAbbreviated")} 0${translate("minutesAbbreviated")}`
                        )
                    ) : (
                        "..."
                    )}
                </div>
            </div>
        </div>
    );
}

function MaintenanceClock() {
    const [rotation, setRotation] = useState(90);

    useEffect(() => {
        const interval = setInterval(() => {
            setRotation((prevRotation) => {
                return prevRotation + 90;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    return (
        <div className={`maintenanceClock ${styles.maintenanceClock}`}>
            <div
                className={`maintenanceClockPointer ${styles.maintenanceClockPointer}`}
                style={{ transform: `rotate(${rotation}deg)` }}
            ></div>
        </div>
    );
}
